import React, { ReactElement, useState, useEffect } from "react";
import { AgGrid, Button, Flex, Text, RadioGroup, RadioButton, toast } from "@jsq/ds/core";
import { SPACING } from "#utils/spacing";
import { exportPanel, exportPanelHeader, exportSection, divider } from "./styles_vanilla.css";
import type { WorkflowTableRow } from "../../../types";

type ExportScope = "all" | "selected";

type CsvColumnWithTransform = { header: string; getValue: (row: WorkflowTableRow) => unknown };

/**
 * Maps each AG Grid column field to a human-readable CSV header and a
 * getValue function. Only columns whose field appears here will ever be
 * included in the export.
 *
 * Note: `statusCategory` is the GPX "Status" column field (the Canopy grid
 * uses the `status` field for its full Status column instead).
 */
const CSV_COLUMN_MAP = new Map<string, CsvColumnWithTransform>([
  ["displayName", { header: "Workflow Name", getValue: (r) => r.displayName }],
  ["currentStepName", { header: "Current Step", getValue: (r) => r.currentStepName }],
  [
    "progress",
    {
      header: "Progress",
      getValue: (r) =>
        r.progress
          ? `${r.progress.completed}/${r.progress.total} (${r.progress.percentage}%)`
          : null,
    },
  ],
  ["workflowType", { header: "Type", getValue: (r) => r.workflowType }],
  ["templateName", { header: "Template", getValue: (r) => r.templateName }],
  ["clientName", { header: "Client", getValue: (r) => r.clientName }],
  ["entityName", { header: "Subject", getValue: (r) => r.entityName }],
  ["assignee", { header: "Assignee", getValue: (r) => r.assignee?.displayName ?? null }],
  ["status", { header: "Status", getValue: (r) => r.status }],
  ["statusCategory", { header: "Status", getValue: (r) => r.statusCategory }],
  [
    "dueDate",
    {
      header: "Due Date",
      getValue: (r) =>
        r.dueDate
          ? new Date(r.dueDate).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : null,
    },
  ],
  ["createdAt", { header: "Created At", getValue: (r) => r.createdAt }],
  ["slaStatus", { header: "SLA Status", getValue: (r) => r.slaStatus }],
]);

function escapeCsvValue(value: unknown): string {
  if (value == null) return "";
  const str = String(value);
  // Neutralize spreadsheet formula injection: prefix dangerous leading chars with a tab
  const safe = /^[=+\-@]/.test(str) ? `\t${str}` : str;
  if (safe.includes(",") || safe.includes('"') || safe.includes("\n") || safe.includes("\t")) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

function rowsToCsv(rows: WorkflowTableRow[], columns: CsvColumnWithTransform[]): string {
  const header = columns.map((c) => c.header).join(",");
  const body = rows.map((row) => columns.map((c) => escapeCsvValue(c.getValue(row))).join(","));
  return [header, ...body].join("\n");
}

function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export type WorkflowExportDataSource = {
  fetchAllRootRows: () => Promise<WorkflowTableRow[]>;
};

export type WorkflowExportPanelProps = {
  api: AgGrid.GridApi<WorkflowTableRow>;
  datasource: WorkflowExportDataSource;
};

export const WorkflowExportPanel = ({
  api,
  datasource,
}: WorkflowExportPanelProps): ReactElement => {
  const [exportScope, setExportScope] = useState<ExportScope>("all");
  const [selectedCount, setSelectedCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const onSelectionChanged = () => {
      const count = api.getSelectedRows().length;
      setSelectedCount(count);
      if (count === 0 && exportScope === "selected") {
        setExportScope("all");
      }
    };

    api.addEventListener("selectionChanged", onSelectionChanged);
    return () => api.removeEventListener("selectionChanged", onSelectionChanged);
  }, [api, exportScope]);

  const handleExport = async () => {
    let rows: WorkflowTableRow[];

    if (exportScope === "selected") {
      rows = api.getSelectedRows();
    } else {
      setIsExporting(true);
      try {
        rows = await datasource.fetchAllRootRows();
      } catch {
        toast.error("Failed to export workflows");
        return;
      } finally {
        setIsExporting(false);
      }
    }

    const visibleCsvColumns = api.getAllDisplayedColumns().flatMap((col: AgGrid.Column) => {
      const field =
        col.getColId() === "ag-Grid-AutoColumn" ? "displayName" : (col.getColDef().field ?? "");
      const entry = CSV_COLUMN_MAP.get(field);
      if (!entry) return [];
      // The auto-group column renders Workflow Name, Current Step, and Progress — include all.
      if (field === "displayName") {
        return [entry, CSV_COLUMN_MAP.get("currentStepName")!, CSV_COLUMN_MAP.get("progress")!];
      }
      return [entry];
    });

    const csv = rowsToCsv(rows, visibleCsvColumns);
    downloadCsv(csv, `workflows-export-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <Flex flexDirection="column" className={exportPanel} data-testid="workflow-export-panel">
      <Flex alignItems="center" justifyContent="space-between" className={exportPanelHeader}>
        <Text variant="regular-md">Export to CSV</Text>
      </Flex>
      <div className={divider} />
      <Flex flexDirection="column" gap={SPACING.MEDIUM} className={exportSection}>
        <RadioGroup
          name="workflow-export-scope"
          onChange={(e) => setExportScope(e.target.value as ExportScope)}
        >
          <RadioButton
            value="all"
            checked={exportScope === "all"}
            name="workflow-export-scope"
            data-testid="export-all-radio"
          >
            <Text variant="secondary-md">All workflows</Text>
          </RadioButton>
          <RadioButton
            value="selected"
            checked={exportScope === "selected"}
            disabled={selectedCount === 0}
            name="workflow-export-scope"
            data-testid="export-selected-radio"
          >
            <Text variant="secondary-md">Selected workflows ({selectedCount})</Text>
          </RadioButton>
        </RadioGroup>
        <Button
          variant="primary"
          onClick={() => void handleExport()}
          icon="download"
          iconPosition="left"
          disabled={isExporting}
          isFullWidth
          data-testid="export-button"
        >
          {isExporting ? "Exporting…" : "Export"}
        </Button>
      </Flex>
    </Flex>
  );
};
